import { QueryClient, useQuery } from "@tanstack/react-query";
import {
	getCombos,
	GetCombosParams,
	getAvailableProducts,
	GetComboProductParams,
	getComboById,
	GetComboBuyerParams,
	getComboBuyers,
	getCombosForProduct,
	getComboBySlug,
	getComboRecommendations,
	getCombosByUserId,
} from "@onair/services/combo";
import {
	COMBO_DETAIL,
	COMBO_RECOMMENDATION,
	GET_COMBOS,
	GET_COMBOS_BY_USER_ID,
	GET_COMBOS_FOR_PRODUCT,
	GET_COMBO_BUYERS,
	GET_COMBO_BY_ID,
	GET_COMBO_BY_SLUG,
	GET_COMBO_PRODUCTS,
} from "../../keys";

const getCombosQuery = (query: GetCombosParams) => {
	return useQuery({
		queryKey: [GET_COMBOS, query],
		queryFn: () => getCombos(query),
	});
};

const getComboBySlugQuery = (slug: string) => {
	return useQuery({
		queryKey: [GET_COMBO_BY_SLUG, slug],
		queryFn: () => getComboBySlug(slug),
	});
};

const getCombosByUserIdQuery = (userId: string) => {
	return useQuery({
		queryKey: [GET_COMBOS_BY_USER_ID, userId],
		queryFn: () => getCombosByUserId(userId),
	});
};

const usePrefetchComboRecommendation = async (
	slug: string,
	queryClient: QueryClient,
) => {
	return queryClient.prefetchQuery({
		queryKey: [COMBO_RECOMMENDATION, slug],
		queryFn: () => getComboRecommendations(slug),
	});
};

const usePrefetchComboBySlug = async (
	slug: string,
	queryClient: QueryClient,
) => {
	return queryClient.prefetchQuery({
		queryKey: [COMBO_DETAIL, slug],
		queryFn: () => getComboBySlug(slug),
	});
};

const usePrefetchComboDetailData = async (
	slug: string,
	queryClient: QueryClient,
) => {
	return Promise.all([
		usePrefetchComboBySlug(slug, queryClient),
		usePrefetchComboRecommendation(slug, queryClient),
	]);
};

const getComboByIdQuery = (id: string) => {
	return useQuery({
		queryKey: [GET_COMBO_BY_ID, id],
		queryFn: () => getComboById(id),
	});
};

const getAvailableProductsQuery = (query: GetComboProductParams) => {
	return useQuery({
		queryKey: [GET_COMBO_PRODUCTS, query],
		queryFn: () => getAvailableProducts(query),
	});
};

const getComboBuyerQuery = (query: GetComboBuyerParams) => {
	return useQuery({
		queryKey: [GET_COMBO_BUYERS, query],
		queryFn: () => getComboBuyers(query),
	});
};

const getCombosForProductQuery = (productId: string) => {
	return useQuery({
		queryKey: [GET_COMBOS_FOR_PRODUCT, productId],
		queryFn: () => getCombosForProduct(productId),
	});
};

export {
	getCombosQuery,
	getAvailableProductsQuery,
	getComboByIdQuery,
	getComboBuyerQuery,
	getCombosForProductQuery,
	getComboBySlugQuery,
	usePrefetchComboDetailData,
	usePrefetchComboBySlug,
	getCombosByUserIdQuery,
};
